import React, { useState, useMemo } from 'react';
import { useMonobankTopup } from '@dopomogai/agent-kit';
import { Card, Button, TextField, Spinner, Alert, Icon } from '@dopomogai/ui';
import { AnimatePresence, motion } from 'framer-motion';

const MIN_CREDIT_PURCHASE = 5;
const QUICK_ADD_AMOUNTS = [10, 25, 50, 100];

/**
 * A self-contained panel for users to top up their credit balance.
 * It handles input, cost calculation, and initiating the Monobank payment flow.
 */
export const CreditTopUpPanel: React.FC = () => {
  const [creditAmount, setCreditAmount] = useState<string>('10');
  const { createInvoice, isLoading, error: apiError } = useMonobankTopup();
  const [inputError, setInputError] = useState<string | null>(null);

  const credits = useMemo(() => {
    const num = parseInt(creditAmount, 10);
    return isNaN(num) ? 0 : num;
  }, [creditAmount]);

  const cost = credits * 1.0; // Assuming 1 credit = 1 USD

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, ''); // Allow only numbers
    setCreditAmount(value);
    if (parseInt(value, 10) < MIN_CREDIT_PURCHASE) {
      setInputError(`Minimum purchase is ${MIN_CREDIT_PURCHASE} credits.`);
    } else {
      setInputError(null);
    }
  };

  const handleQuickAdd = (amount: number) => {
    const newAmount = credits + amount;
    setCreditAmount(String(newAmount));
     if (newAmount < MIN_CREDIT_PURCHASE) {
      setInputError(`Minimum purchase is ${MIN_CREDIT_PURCHASE} credits.`);
    } else {
      setInputError(null);
    }
  };

  const handlePurchase = () => {
    if (isLoading || credits < MIN_CREDIT_PURCHASE) {
      return;
    }
    // The hook will handle the redirect on success
    createInvoice({ credits });
  };

  return (
    <Card title="Top Up Your Balance" icon={<Icon name="Zap" />}>
      <div className="space-y-6 p-2">
        <div>
          <TextField
            label="Credits to Purchase"
            id="credit-amount"
            type="text" // Use text to allow empty state, but filter input
            value={creditAmount}
            onChange={handleAmountChange}
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder={`e.g., ${MIN_CREDIT_PURCHASE * 2}`}
            errorText={inputError ?? undefined}
          />
          <div className="flex items-center gap-2 mt-2">
            {QUICK_ADD_AMOUNTS.map((amount) => (
              <Button
                key={amount}
                size="sm"
                variant="outline"
                text={`+${amount}`}
                onClick={() => handleQuickAdd(amount)}
              />
            ))}
          </div>
        </div>

        <div className="text-center my-4 p-4 bg-slate-100 rounded-xl border-2 border-black">
          <p className="text-sm font-medium text-slate-600 mb-1">Total Cost</p>
          <p className="text-4xl font-extrabold text-emerald-600">
            ${cost.toFixed(2)}
            <span className="text-lg font-semibold text-slate-500 ml-2">USD</span>
          </p>
        </div>
        
        <AnimatePresence>
          {apiError && (
             <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <Alert type="error" title="Payment Error" message={apiError.message} />
             </motion.div>
          )}
        </AnimatePresence>

        <Button
          text="Proceed to Payment"
          onClick={handlePurchase}
          disabled={isLoading || !!inputError}
          fullWidth
          size="lg"
          variant="primary"
          icon={isLoading ? <Spinner size="sm" /> : <Icon name="CreditCard" />}
        />
        <div className="flex justify-center items-center gap-2 pt-2">
            <Icon name="Lock" className="h-4 w-4 text-slate-400" />
            <p className="text-xs text-slate-500">
                Secure payment processed by Monobank
            </p>
        </div>
      </div>
    </Card>
  );
};